import { useState } from "react";

interface Plan {
  name: string;
  price: number;
  label: string;
}

interface PaymentFormProps {
  plan: Plan;
  onBack: () => void;
  onSuccess: () => void;
}

type PayMethod = "credit" | "debit" | "wechat" | "alipay";

export default function PaymentForm({ plan, onBack, onSuccess }: PaymentFormProps) {
  const [payMethod, setPayMethod] = useState<PayMethod>("credit");
  const [cardNumber, setCardNumber] = useState("");
  const [cardName, setCardName] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvv, setCvv] = useState("");
  const [loading, setLoading] = useState(false);
  const [qrScanned, setQrScanned] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const formatCardNumber = (val: string) => {
    const digits = val.replace(/\D/g, "").slice(0, 16);
    return digits.replace(/(.{4})/g, "$1 ").trim();
  };

  const formatExpiry = (val: string) => {
    const digits = val.replace(/\D/g, "").slice(0, 4);
    if (digits.length >= 3) return digits.slice(0, 2) + "/" + digits.slice(2);
    return digits;
  };

  const detectCardBrand = (num: string) => {
    const n = num.replace(/\s/g, "");
    if (n.startsWith("4")) return { brand: "Visa", icon: "ri-visa-line" };
    if (n.startsWith("5") || n.startsWith("2")) return { brand: "Mastercard", icon: "ri-mastercard-line" };
    if (n.startsWith("3")) return { brand: "Amex", icon: "ri-bank-card-line" };
    return { brand: "", icon: "ri-bank-card-line" };
  };

  const isCardMethod = payMethod === "credit" || payMethod === "debit";

  const validate = () => {
    const errs: Record<string, string> = {};
    if (isCardMethod) {
      if (!cardName.trim()) errs.cardName = "Cardholder name is required";
      if (cardNumber.replace(/\s/g, "").length < 16) errs.cardNumber = "Enter a valid 16-digit card number";
      if (!expiry || expiry.length < 5) errs.expiry = "Enter a valid expiry date (MM/YY)";
      if (!cvv || cvv.length < 3) errs.cvv = "Enter a valid CVV";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      onSuccess();
    }, 1800);
  };

  const handleQrPay = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setQrScanned(true);
    }, 2000);
    setTimeout(() => {
      onSuccess();
    }, 3200);
  };

  const cardBrand = detectCardBrand(cardNumber);

  const payMethods = [
    { key: "credit" as PayMethod, label: "Credit Card", icon: "ri-bank-card-line", color: "text-gray-700" },
    { key: "debit" as PayMethod, label: "Debit Card", icon: "ri-bank-card-2-line", color: "text-gray-700" },
    { key: "wechat" as PayMethod, label: "WeChat Pay", icon: "ri-wechat-pay-line", color: "text-green-600" },
    { key: "alipay" as PayMethod, label: "Alipay", icon: "ri-alipay-line", color: "text-blue-500" },
  ];

  return (
    <div className="bg-white rounded-2xl overflow-hidden">
      {/* Order summary banner */}
      <div className="bg-gradient-to-r from-amber-400 to-orange-400 p-6 text-white">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm mb-4 cursor-pointer transition-colors"
        >
          <i className="ri-arrow-left-line"></i> Back to plans
        </button>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white/80 text-sm">You selected</p>
            <h3 className="text-2xl font-extrabold">{plan.label} Membership</h3>
            <p className="text-white/80 text-sm mt-0.5">Billed monthly · Cancel anytime</p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-extrabold">${plan.price}</div>
            <div className="text-white/80 text-sm">/month</div>
          </div>
        </div>
      </div>

      <div className="p-6 md:p-8">
        {/* Payment method selector */}
        <div className="mb-6">
          <p className="text-sm font-semibold text-gray-700 mb-3">Payment Method</p>
          <div className="grid grid-cols-2 gap-3">
            {payMethods.map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => { setPayMethod(m.key); setQrScanned(false); setErrors({}); }}
                className={`flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-semibold text-sm transition-all cursor-pointer whitespace-nowrap ${
                  payMethod === m.key
                    ? "border-amber-400 bg-amber-50 text-amber-600"
                    : "border-gray-200 text-gray-500 hover:border-amber-300 hover:bg-gray-50"
                }`}
              >
                <i className={`${m.icon} text-lg ${payMethod === m.key ? "text-amber-500" : m.color}`}></i>
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {/* ── Credit / Debit Card ── */}
          {isCardMethod && (
            <>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {payMethod === "credit" ? "Credit Card" : "Debit Card"} Details
                </span>
                <div className="flex items-center gap-1.5 ml-auto">
                  <i className="ri-visa-line text-xl text-gray-500"></i>
                  <i className="ri-mastercard-line text-xl text-gray-500"></i>
                  <i className="ri-bank-card-line text-xl text-gray-500"></i>
                </div>
              </div>

              {/* Card number */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Card Number</label>
                <div className="relative">
                  <input
                    type="text"
                    value={cardNumber}
                    onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                    placeholder="1234 5678 9012 3456"
                    className={`w-full border rounded-xl px-4 py-3 text-sm pr-12 focus:outline-none transition-colors ${
                      errors.cardNumber ? "border-red-400 bg-red-50" : "border-gray-200 focus:border-amber-400"
                    }`}
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400">
                    <i className={`${cardBrand.icon} text-lg`}></i>
                  </div>
                </div>
                {errors.cardNumber && <p className="text-red-500 text-xs mt-1">{errors.cardNumber}</p>}
              </div>

              {/* Cardholder name */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Cardholder Name</label>
                <input
                  type="text"
                  value={cardName}
                  onChange={(e) => setCardName(e.target.value)}
                  placeholder="Name as it appears on card"
                  className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none transition-colors ${
                    errors.cardName ? "border-red-400 bg-red-50" : "border-gray-200 focus:border-amber-400"
                  }`}
                />
                {errors.cardName && <p className="text-red-500 text-xs mt-1">{errors.cardName}</p>}
              </div>

              {/* Expiry + CVV */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Expiry Date</label>
                  <input
                    type="text"
                    value={expiry}
                    onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                    placeholder="MM/YY"
                    className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none transition-colors ${
                      errors.expiry ? "border-red-400 bg-red-50" : "border-gray-200 focus:border-amber-400"
                    }`}
                  />
                  {errors.expiry && <p className="text-red-500 text-xs mt-1">{errors.expiry}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">CVV / CVC</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={cvv}
                      onChange={(e) => setCvv(e.target.value.replace(/\D/g, "").slice(0, 4))}
                      placeholder="•••"
                      className={`w-full border rounded-xl px-4 py-3 text-sm pr-10 focus:outline-none transition-colors ${
                        errors.cvv ? "border-red-400 bg-red-50" : "border-gray-200 focus:border-amber-400"
                      }`}
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                      <i className="ri-question-line text-sm"></i>
                    </div>
                  </div>
                  {errors.cvv && <p className="text-red-500 text-xs mt-1">{errors.cvv}</p>}
                </div>
              </div>

              <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-4 py-3">
                <i className="ri-shield-keyhole-line text-green-500 text-lg shrink-0"></i>
                <p className="text-xs text-gray-500">
                  Your payment is secured with 256-bit SSL encryption. We never store your card details.
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-amber-400 hover:bg-amber-500 disabled:opacity-60 text-white font-bold py-4 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-base mt-2"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin text-lg"></i>
                    Processing Payment...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-lock-line"></i>
                    Pay ${plan.price}/month — Activate {plan.label}
                  </span>
                )}
              </button>
            </>
          )}

          {/* ── WeChat Pay ── */}
          {payMethod === "wechat" && (
            <div className="flex flex-col items-center gap-5">
              <div className="w-full bg-green-50 border border-green-100 rounded-2xl p-6 flex flex-col items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center bg-green-500 rounded-full">
                    <i className="ri-wechat-pay-line text-white text-lg"></i>
                  </div>
                  <span className="font-bold text-gray-800 text-base">WeChat Pay</span>
                </div>

                {/* Simulated QR code */}
                <div className={`relative w-44 h-44 bg-white border-2 rounded-xl flex items-center justify-center transition-all duration-500 ${qrScanned ? "border-green-400" : "border-gray-200"}`}>
                  {qrScanned ? (
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full">
                        <i className="ri-checkbox-circle-fill text-green-500 text-3xl"></i>
                      </div>
                      <span className="text-green-600 font-bold text-sm">Payment Confirmed!</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 p-3">
                      {/* QR pattern simulation */}
                      <div className="grid grid-cols-7 gap-0.5">
                        {Array.from({ length: 49 }).map((_, i) => (
                          <div
                            key={i}
                            className={`w-4 h-4 rounded-sm ${
                              [0,1,2,3,4,5,6,7,13,14,20,21,27,28,34,35,41,42,43,44,45,46,48,8,15,22,29,36,10,17,24,31,38,11,18,25,32,39].includes(i)
                                ? "bg-gray-800"
                                : "bg-white"
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="text-center">
                  <p className="text-sm font-semibold text-gray-700">
                    Amount: <span className="text-green-600">${plan.price} USD</span>
                  </p>
                  {!qrScanned ? (
                    <p className="text-xs text-gray-500 mt-1">
                      Open WeChat &rarr; Scan QR Code to pay
                    </p>
                  ) : (
                    <p className="text-xs text-green-600 font-semibold mt-1">
                      Redirecting to your dashboard...
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-4 py-3 w-full">
                <i className="ri-shield-keyhole-line text-green-500 text-lg shrink-0"></i>
                <p className="text-xs text-gray-500">
                  Secured by WeChat Pay. QR code expires in 15 minutes.
                </p>
              </div>

              {!qrScanned && (
                <button
                  type="button"
                  onClick={handleQrPay}
                  disabled={loading}
                  className="w-full bg-green-500 hover:bg-green-600 disabled:opacity-60 text-white font-bold py-4 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-base"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin text-lg"></i>
                      Waiting for scan...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-qr-code-line"></i>
                      I&apos;ve Scanned the QR Code
                    </span>
                  )}
                </button>
              )}
            </div>
          )}

          {/* ── Alipay ── */}
          {payMethod === "alipay" && (
            <div className="flex flex-col items-center gap-5">
              <div className="w-full bg-sky-50 border border-sky-100 rounded-2xl p-6 flex flex-col items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center bg-sky-500 rounded-full">
                    <i className="ri-alipay-line text-white text-lg"></i>
                  </div>
                  <span className="font-bold text-gray-800 text-base">Alipay</span>
                </div>

                {/* Simulated QR code */}
                <div className={`relative w-44 h-44 bg-white border-2 rounded-xl flex items-center justify-center transition-all duration-500 ${qrScanned ? "border-sky-400" : "border-gray-200"}`}>
                  {qrScanned ? (
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-14 h-14 flex items-center justify-center bg-sky-100 rounded-full">
                        <i className="ri-checkbox-circle-fill text-sky-500 text-3xl"></i>
                      </div>
                      <span className="text-sky-600 font-bold text-sm">Payment Confirmed!</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 p-3">
                      {/* QR pattern simulation */}
                      <div className="grid grid-cols-7 gap-0.5">
                        {Array.from({ length: 49 }).map((_, i) => (
                          <div
                            key={i}
                            className={`w-4 h-4 rounded-sm ${
                              [0,1,2,3,4,5,6,7,13,14,20,21,27,28,34,35,41,42,43,44,45,46,48,9,16,23,30,37,12,19,26,33,40,11,18,25,32,39].includes(i)
                                ? "bg-sky-700"
                                : "bg-white"
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="text-center">
                  <p className="text-sm font-semibold text-gray-700">
                    Amount: <span className="text-sky-600">${plan.price} USD</span>
                  </p>
                  {!qrScanned ? (
                    <p className="text-xs text-gray-500 mt-1">
                      Open Alipay &rarr; Scan QR Code to pay
                    </p>
                  ) : (
                    <p className="text-xs text-sky-600 font-semibold mt-1">
                      Redirecting to your dashboard...
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-4 py-3 w-full">
                <i className="ri-shield-keyhole-line text-green-500 text-lg shrink-0"></i>
                <p className="text-xs text-gray-500">
                  Secured by Alipay. QR code expires in 15 minutes.
                </p>
              </div>

              {!qrScanned && (
                <button
                  type="button"
                  onClick={handleQrPay}
                  disabled={loading}
                  className="w-full bg-sky-500 hover:bg-sky-600 disabled:opacity-60 text-white font-bold py-4 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-base"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin text-lg"></i>
                      Waiting for scan...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-qr-code-line"></i>
                      I&apos;ve Scanned the QR Code
                    </span>
                  )}
                </button>
              )}
            </div>
          )}

          <p className="text-center text-xs text-gray-400">
            By subscribing you agree to our{" "}
            <span className="text-amber-500 cursor-pointer hover:underline">Terms of Service</span> and{" "}
            <span className="text-amber-500 cursor-pointer hover:underline">Privacy Policy</span>. Cancel anytime.
          </p>
        </form>
      </div>
    </div>
  );
}
